/**
 * The error codes the billing surface answers with (U3).
 *
 * Declared up front rather than promoted from api-local literals later — the
 * lesson `NOTIFICATION_ERROR_CODES` recorded, and `COURSE_ERROR_CODES` followed.
 *
 * **A member-facing refusal here is a state, not a failure.** "You are not
 * subscribed" is the paywall's cue, not an error to log — which is why
 * `entitlementRequired` is a 403 the client is expected to catch and turn into a
 * screen, and why it carries which capability was wanted.
 */
export const BILLING_ERROR_CODES = {
  /*
   * ---- the catalogue (staff) ----
   */

  /** No such subscription product. 404. */
  productNotFound: 'billing_product_not_found',
  /**
   * A product with this key already exists. 409.
   *
   * A separate code from a generic conflict because the key is the one field a
   * staff member cannot change afterwards — the form has to say so at the point
   * of collision, not after the product is created.
   */
  productKeyTaken: 'billing_product_key_taken',
  /** Another live product already carries this App Store / Play id. 409 — a store id must name one plan. */
  storeProductIdTaken: 'billing_store_product_id_taken',
  /**
   * The product is not sold to this member's audience. 404 rather than 403.
   *
   * A member has no business learning that a plan exists for the other gender;
   * "no such plan" is also the honest answer for a withdrawn one.
   */
  productUnavailable: 'billing_product_unavailable',
  /**
   * A patch names a field the product's kind does not have — a duration on a
   * fee, a purpose on a plan. 422: the body is well-formed and wrong for this row.
   */
  productKindMismatch: 'billing_product_kind_mismatch',
  /** A fee product was named where a plan was needed — a promo grant, a period. 422. */
  productNotGrantable: 'billing_product_not_grantable',
  /**
   * A fee is switched on for this member's scope, but no active `fee` product
   * carries its purpose for their audience — a configuration gap, answered 409
   * so the operator, not the member, is told.
   */
  feeOfferMissing: 'billing_fee_offer_missing',

  /*
   * ---- entitlements ----
   */

  /** No such entitlement. 404. */
  entitlementNotFound: 'billing_entitlement_not_found',
  /**
   * The member already holds a live entitlement from this store. 409.
   *
   * The refusal branch of the grant **claim** (N27): two racing grants must not
   * both land, or a member ends up with two overlapping subscriptions from one
   * store and no way to say which one a renewal extends.
   */
  entitlementAlreadyActive: 'billing_entitlement_already_active',
  /**
   * The entitlement is no longer in a state that can be revoked. 409.
   *
   * The refusal branch of the revoke claim. Revoking is irreversible, so a
   * second attempt does **nothing** rather than re-stamping a `revokedAt` that
   * would misreport when access actually ended.
   */
  entitlementNotClaimable: 'billing_entitlement_not_claimable',
  /**
   * The caller is not entitled to what they asked for. 403.
   *
   * The one refusal a healthy client will meet routinely: it is what the paywall
   * is *for*. Carries the capability under {@link BILLING_CAPABILITY_DETAIL_KEY}
   * so the client can say which feature needs a plan rather than showing a
   * generic wall.
   */
  entitlementRequired: 'billing_entitlement_required',
  /**
   * A paid channel asked for an entitlement with no end date. 422.
   *
   * Only a `promo` comp may be open-ended. A store or direct grant with a null
   * period never expires and the sweep skips it by design, so the mistake is
   * permanent free access with nothing to notice it — which is why this is
   * refused at the door rather than defaulted to something plausible.
   */
  entitlementPeriodRequired: 'billing_entitlement_period_required',

  /*
   * ---- the RevenueCat webhook ----
   */

  /**
   * The delivery did not carry the configured webhook secret. 401.
   *
   * Checked **before** the body is parsed, so an unauthenticated caller learns
   * nothing about the payload we expect.
   */
  webhookUnauthorized: 'billing_webhook_unauthorized',
  /**
   * The body is not a RevenueCat event. 400, and deliberately **not** recorded:
   * with no event id there is nothing to key idempotency on.
   */
  webhookPayloadInvalid: 'billing_webhook_payload_invalid',

  /*
   * ---- the member sync ----
   */

  /** RevenueCat could not be asked, or refused the key. 502 — the api is fine. */
  revenuecatUnavailable: 'billing_revenuecat_unavailable',
  /** More syncs this minute than the ceiling allows. 429. */
  syncRateLimited: 'billing_sync_rate_limited',
  /** The sync's own guard could not be consulted. 503. */
  syncUnavailable: 'billing_sync_unavailable',

  /*
   * ---- the billing trail (staff) ----
   */

  /**
   * No such recorded delivery. 404.
   *
   * Reachable only from the payload read, which is the one route that takes an
   * id. A trail row is never deleted by an endpoint, so in practice this is a
   * stale link or a soft-deleted row — both of which are "it is not there any
   * more" rather than "you may not see it", and neither is worth distinguishing
   * to the operator.
   */
  billingEventNotFound: 'billing_event_not_found',

  /*
   * ---- IAP offer codes ----
   */

  /** No such offer-code mapping. 404. */
  offerCodeMappingNotFound: 'billing_offer_code_mapping_not_found',
  /**
   * This rule already maps to a code for this product and store. 409.
   *
   * One code per (rule, product, store): a second would make "which discount
   * does this purchase get?" ambiguous at the moment a member is being charged.
   */
  offerCodeMappingConflict: 'billing_offer_code_mapping_conflict',
  /** The discount rule named by a mapping does not exist. 422. */
  discountRuleNotFound: 'billing_discount_rule_not_found',

  /*
   * ---- direct payment orders (staff) ----
   */

  /** No such order. 404. */
  orderNotFound: 'billing_order_not_found',
  /**
   * The order is not in a state this transition can act on. 409.
   *
   * The refusal branch of **every** claim on the order machine — submit,
   * confirm, reject, refund — deliberately as one code rather than four. What a
   * staff member needs to know is that somebody else already decided this order,
   * and the current status (which the refreshed row carries) says which way. Four
   * codes would invite four copies of the same sentence and imply a difference
   * that is not there.
   *
   * It is what makes a double confirm **do nothing**: no second entitlement, no
   * second audit row, no re-stamped `confirmedAt`.
   */
  orderNotClaimable: 'billing_order_not_claimable',
  /**
   * This member has no price for this plan in their gender scope. 422.
   *
   * `billing.directPrices` is a record keyed by product, and a plan absent from
   * it is not free — it is not sold on this channel. Defaulting to zero would
   * hand out subscriptions for nothing, and defaulting to the global price would
   * silently undo the per-gender pricing U15 exists to express.
   */
  orderPriceUnavailable: 'billing_order_price_unavailable',
  /**
   * A discount the order was quoted could not be claimed. 409.
   *
   * Raised at **submit**, when the grants are reserved. The quote is not a
   * reservation, so a grant can expire, be revoked with a ban, or be spent by
   * another order in between — all three arrive here. The order stays a draft
   * and nothing is half-claimed: whatever was taken is given back before this
   * is thrown.
   */
  orderDiscountUnavailable: 'billing_order_discount_unavailable',

  /*
   * ---- payment evidence (staff) ----
   */

  /**
   * The order already has a receipt attached. 409.
   *
   * The refusal branch of the attach **claim**, and it is a refusal rather than a
   * replacement on purpose — the same argument the `singleton` media kinds make:
   * overwriting would destroy a receipt the moment a second upload was merely
   * *started*, and an abandoned retake would leave a confirmed payment with no
   * proof behind it. Replacing is detach-then-attach, so the destruction is an
   * act somebody chose.
   */
  orderEvidencePresent: 'billing_order_evidence_present',
  /**
   * There is no receipt on this order to detach. 409.
   *
   * The other refusal branch of the same claim — a second detach, or a colleague
   * who detached first. It does **nothing** rather than scheduling a purge for an
   * id it did not take (N27).
   */
  orderEvidenceAbsent: 'billing_order_evidence_absent',
  /**
   * The order has been decided, so its receipt can no longer be detached. 422.
   *
   * A *state* refusal rather than a claim failure, which is why it is not
   * {@link BILLING_ERROR_CODES.orderNotClaimable}: nothing raced, and no retry or
   * re-read will change the answer. Once a confirmation, a rejection or a refund
   * was decided **on** this evidence, the evidence is part of that record.
   * Attaching stays open at every live status — a receipt arriving late completes
   * the file, and adding a record is never destructive.
   */
  orderEvidenceLocked: 'billing_order_evidence_locked',
  /**
   * The asset offered as a receipt is not one. 422.
   *
   * The upload endpoints mint and finalize the asset themselves, so this is
   * unreachable through the intended flow; it exists because binding a
   * caller-named uuid into `evidence_media_id` without checking its kind is how a
   * billing operator would come to point an order at a member's identity document
   * — and then destroy it by detaching.
   */
  orderEvidenceKindInvalid: 'billing_order_evidence_kind_invalid',

  /*
   * ---- the member's own door (F3) ----
   */

  /**
   * Direct payment is switched off for this deployment (`billing.direct.enabled`).
   * 403: the member is refused a door, not told a state of their own.
   */
  directPaymentClosed: 'billing_direct_payment_closed',
  /**
   * The member already has an open order for this plan. 409.
   *
   * The refusal branch of the submit **claim** — a partial unique index over
   * `(user, product)` for open member orders — so two taps, or a retry after a
   * timed-out submit, cannot file a second receipt for one transfer. The client
   * re-reads and shows the one that is pending.
   */
  orderPending: 'billing_order_pending',
  /**
   * The asset offered as a receipt is not the member's own ready
   * `payment_receipt`. 422 — a wrong kind, somebody else's upload, or one that
   * never finalized all read the same way, deliberately: the describe query
   * answers by owner and says nothing about assets that are not theirs.
   */
  orderReceiptNotAcceptable: 'billing_order_receipt_not_acceptable',
  /**
   * The receipt is the MEMBER's submission and is never detached by staff. 422.
   *
   * A member order is decided on its receipt — confirmed or rejected — and
   * either way the receipt stays part of that record. Taking it off would leave
   * a member-filed order with no evidence and no way for the member to know.
   */
  orderReceiptIsMembers: 'billing_order_receipt_is_members',

  // ---- the payments ledger (staff) ----

  /** No such ledger row. 404. */
  paymentNotFound: 'billing_payment_not_found',
} as const;

export type BillingErrorCode = (typeof BILLING_ERROR_CODES)[keyof typeof BILLING_ERROR_CODES];
